// src/components/multiplayer/OnlineUsersPanel.tsx
// FIXED VERSION - With proper error handling

import React, { useState, useEffect } from "react";
import "./OnlineUsersPanel.css";

interface OnlineUser {
  userId: string;
  username: string;
  status: "available" | "do_not_disturb" | "hosting" | "in_game";
  acceptInvitations: boolean;
  connectedAt: string;
  lastActivity: string;
  inRoom: string | null;
  // Player statistics
  rank?: number;
  rating?: number;
  gamesPlayed?: number;
  winsPercentage?: number;
  // Hosting info
  hostingGameVariant?: string;
}

interface OnlineUsersPanelProps {
  currentUserId?: string;
  currentUsername: string;
  serverUrl?: string;
  onChallengeUser?: (userId: string, username: string) => void;
  onJoinGame?: (userId: string, username: string, gameVariant?: string) => void;
  // Current user's hosting status
  isCurrentUserHosting?: boolean;
  currentUserGameVariant?: string;
  onShowToast?: (message: string) => void;
}

export const OnlineUsersPanel: React.FC<OnlineUsersPanelProps> = ({
  currentUserId,
  currentUsername,
  serverUrl = "",
  onChallengeUser,
  onJoinGame,
  isCurrentUserHosting = false,
  currentUserGameVariant,
  onShowToast,
}) => {
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const INITIAL_DISPLAY_COUNT = 5;

  const fetchOnlineUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const API_BASE_URL = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${API_BASE_URL}/api/multiplayer/users/online`);

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("✅ Fetched online users:", data);

      // ⭐ SAFETY CHECK: Ensure data is an array
      if (!Array.isArray(data)) {
        console.error("❌ API returned non-array:", data);
        setOnlineUsers([]);
        setError("Invalid data format received");
        return;
      }

      // ⭐ SAFETY CHECK: Filter out invalid entries (keep current user to show they're online)
      const validUsers = data.filter((user: any) => {
        // Check if user object has required properties
        if (!user || typeof user !== 'object') {
          console.warn("⚠️ Invalid user object:", user);
          return false;
        }

        // Must have userId and username
        if (!user.userId || !user.username) {
          console.warn("⚠️ User missing required fields:", user);
          return false;
        }

        // Keep all users including current user (they can see themselves as online)
        return true;
      });

      console.log(`✅ Filtered to ${validUsers.length} valid users`);
      setOnlineUsers(validUsers);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Unknown error";
      console.error("❌ Error fetching online users:", err);
      setError(errorMsg);
      setOnlineUsers([]); // ⭐ Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();

    // Poll every 60 seconds
    const interval = setInterval(fetchOnlineUsers, 60000);

    return () => clearInterval(interval);
  }, [serverUrl, currentUserId]);

  const getStatusIcon = (status: OnlineUser["status"]) => {
    switch (status) {
      case "available":
        return "🟢";
      case "do_not_disturb":
        return "🔴";
      case "hosting":
        return "🟡";
      case "in_game":
        return "🟣";
      default:
        return "⚪";
    }
  };

  const getStatusLabel = (status: OnlineUser["status"]) => {
    switch (status) {
      case "available":
        return "Available";
      case "do_not_disturb":
        return "Do Not Disturb";
      case "hosting":
        return "Hosting Game";
      case "in_game":
        return "In Game";
      default:
        return "Unknown";
    }
  };

  const handleChallenge = (user: OnlineUser) => {
    if (onChallengeUser) {
      console.log(`⚔️ Challenging user: ${user.username}`);
      onChallengeUser(user.userId, user.username);
    }
  };

  const canChallenge = (user: OnlineUser) => {
    return user.acceptInvitations && user.status === "available" && !user.inRoom;
  };

  // Shared button styles
  const buttonStyle = {
    showAll: {
      background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: 700,
      cursor: "pointer",
      boxShadow: "0 2px 6px rgba(33, 150, 243, 0.3)",
      minHeight: "44px"
    } as React.CSSProperties,
    refresh: {
      background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "12px 24px",
      fontSize: "14px",
      fontWeight: 700,
      cursor: "pointer",
      boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
      minHeight: "44px"
    } as React.CSSProperties
  };

  // Header with buttons component
  const renderHeader = (count: number | string) => (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "6px",
      paddingBottom: "6px",
      borderBottom: "1px solid #e0e0e0"
    }}>
      <span style={{ fontWeight: 600, color: "#333", fontSize: "22px" }}>
        {typeof count === 'number' ? `${count} player${count !== 1 ? 's' : ''} online` : count}
      </span>
      <div style={{ display: "flex", gap: "10px" }}>
        <button
          onClick={() => {
            fetchOnlineUsers();
            if (onShowToast) onShowToast("Refreshing player list...");
          }}
          style={buttonStyle.refresh}
          title="Refresh"
        >
          Refresh
        </button>
      </div>
    </div>
  );

  // Loading state
  if (loading && onlineUsers.length === 0) {
    return (
      <div style={{ width: "100%" }}>
        {renderHeader("Loading...")}
        <div style={{ textAlign: "center", padding: "20px", color: "#666", fontSize: "18px" }}>
          Loading players...
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ width: "100%" }}>
        {renderHeader("Error")}
        <div style={{ textAlign: "center", padding: "20px", color: "#d32f2f", fontSize: "18px" }}>
          ⚠️ {error}
        </div>
      </div>
    );
  }

  // Create current user object to always display
  const currentUserEntry: OnlineUser = {
    userId: currentUserId || "current-user",
    username: currentUsername,
    status: isCurrentUserHosting ? "hosting" : "available",
    acceptInvitations: true,
    connectedAt: new Date().toISOString(),
    lastActivity: new Date().toISOString(),
    inRoom: null,
    hostingGameVariant: currentUserGameVariant
  };

  // Combine: If current user not in API results, add them
  const allUsers = onlineUsers.some(u => u.userId === currentUserId)
    ? onlineUsers
    : [currentUserEntry, ...onlineUsers];

  // Apply show all / limited display
  const displayUsers = showAll ? allUsers : allUsers.slice(0, INITIAL_DISPLAY_COUNT);
  const hasMoreUsers = allUsers.length > INITIAL_DISPLAY_COUNT;

  // Render users - compact single-line layout (always shows at least current user)
  return (
    <div style={{ width: "100%" }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "6px",
        paddingBottom: "6px",
        borderBottom: "1px solid #e0e0e0"
      }}>
        <span style={{ fontWeight: 600, color: "#333", fontSize: "22px" }}>
          {allUsers.length} player{allUsers.length !== 1 ? 's' : ''} online
          {!showAll && hasMoreUsers && ` (showing ${displayUsers.length})`}
        </span>
        <div style={{ display: "flex", gap: "10px" }}>
          {hasMoreUsers && (
            <button
              onClick={() => setShowAll(!showAll)}
              style={{
                background: showAll
                  ? "linear-gradient(135deg, #757575 0%, #616161 100%)"
                  : "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                color: "white",
                border: "none",
                borderRadius: "6px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: showAll
                  ? "0 2px 6px rgba(117, 117, 117, 0.3)"
                  : "0 2px 6px rgba(33, 150, 243, 0.3)",
                minHeight: "44px"
              }}
            >
              {showAll ? "Show Less" : `Show All (${allUsers.length})`}
            </button>
          )}
          <button
            onClick={() => {
              fetchOnlineUsers();
              if (onShowToast) onShowToast("Refreshing player list...");
            }}
            style={{
              background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
              color: "white",
              border: "none",
              borderRadius: "6px",
              padding: "12px 24px",
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
              minHeight: "44px"
            }}
            title="Refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        {displayUsers.map((user) => {
          // ⭐ SAFETY CHECK: Skip if user is invalid
          if (!user || !user.userId || !user.username) {
            console.warn("⚠️ Skipping invalid user in render:", user);
            return null;
          }

          const isCurrentUser = user.userId === currentUserId;

          return (
            <div
              key={user.userId}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 10px",
                background: isCurrentUser
                  ? (isCurrentUserHosting ? "#fff3e0" : "#e8f5e9")  // Light orange when hosting, light green otherwise
                  : (user.status === "hosting" ? "#fff8e1" : "#fafafa"),  // Light yellow-orange for other hosts
                borderRadius: "6px",
                border: isCurrentUser
                  ? (isCurrentUserHosting ? "2px solid #FF9800" : "2px solid #4CAF50")  // Orange border when hosting
                  : (user.status === "hosting" ? "2px solid #FFB74D" : "1px solid #e0e0e0")
              }}
            >
              {/* Left side: Status dot + Username */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "24px" }}>
                  {isCurrentUser && isCurrentUserHosting ? "🟡" : getStatusIcon(user.status)}
                </span>
                <span style={{ fontWeight: 600, fontSize: "25px", color: "#333" }}>
                  {user.username}
                  {isCurrentUser && <span style={{ color: "#4CAF50", marginLeft: "8px" }}>(You)</span>}
                </span>
                <span style={{ fontSize: "20px", color: "#888" }}>
                  {isCurrentUser && isCurrentUserHosting ? (
                    <>
                      (Hosting{currentUserGameVariant ? `: ${currentUserGameVariant}` : ""})
                    </>
                  ) : user.status === "hosting" && user.hostingGameVariant ? (
                    <>
                      (Hosting: {user.hostingGameVariant})
                    </>
                  ) : (
                    `(${getStatusLabel(user.status)})`
                  )}
                </span>
              </div>

              {/* Stats in the middle */}
              <div style={{ display: "flex", gap: "20px", fontSize: "22px", color: "#666" }}>
                <span>Rank: <strong style={{ color: "#4CAF50" }}>{user.rank ?? 'N/A'}</strong></span>
                <span>Rating: <strong style={{ color: "#4CAF50" }}>{user.rating ?? 'N/A'}</strong></span>
                <span>Games: <strong style={{ color: "#4CAF50" }}>{user.gamesPlayed ?? 0}</strong></span>
                <span>Wins: <strong style={{ color: "#4CAF50" }}>
                  {user.winsPercentage !== undefined ? `${user.winsPercentage.toFixed(1)}%` : 'N/A'}
                </strong></span>
              </div>

              {/* Right side: Action buttons (not shown for current user) */}
              {!isCurrentUser && (
                <>
                  {/* Challenge button - for available users */}
                  {canChallenge(user) && onChallengeUser && (
                    <button
                      onClick={() => handleChallenge(user)}
                      style={{
                        background: "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "12px 24px",
                        fontSize: "14px",
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: "0 2px 6px rgba(76, 175, 80, 0.3)",
                        minHeight: "44px"
                      }}
                    >
                      Challenge
                    </button>
                  )}

                  {/* Join Game button - for hosting users */}
                  {user.status === "hosting" && onJoinGame && (
                    <button
                      onClick={() => onJoinGame(user.userId, user.username, user.hostingGameVariant)}
                      style={{
                        background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        padding: "12px 24px",
                        fontSize: "14px",
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: "0 2px 6px rgba(33, 150, 243, 0.3)",
                        minHeight: "44px"
                      }}
                    >
                      Join Game
                    </button>
                  )}

                  {/* Disabled buttons for other statuses */}
                  {user.status === "do_not_disturb" && (
                    <button disabled style={{ background: "#e0e0e0", color: "#999", border: "none", borderRadius: "6px", padding: "12px 24px", fontSize: "14px", fontWeight: 600, cursor: "not-allowed", minHeight: "44px" }}>
                      Busy
                    </button>
                  )}
                  {user.status === "in_game" && (
                    <button disabled style={{ background: "#e0e0e0", color: "#999", border: "none", borderRadius: "6px", padding: "12px 24px", fontSize: "14px", fontWeight: 600, cursor: "not-allowed", minHeight: "44px" }}>
                      In Game
                    </button>
                  )}
                  {user.status === "available" && user.inRoom && (
                    <button disabled style={{ background: "#e0e0e0", color: "#999", border: "none", borderRadius: "6px", padding: "12px 24px", fontSize: "14px", fontWeight: 600, cursor: "not-allowed", minHeight: "44px" }}>
                      Busy
                    </button>
                  )}
                </>
              )}
              {/* Show status badge for current user instead of Challenge button */}
              {isCurrentUser && (
                <span style={{
                  background: isCurrentUserHosting
                    ? "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)"
                    : "linear-gradient(135deg, #4CAF50 0%, #388E3C 100%)",
                  color: "white",
                  borderRadius: "6px",
                  padding: "12px 24px",
                  fontSize: "14px",
                  fontWeight: 700,
                  minHeight: "44px",
                  display: "flex",
                  alignItems: "center"
                }}>
                  {isCurrentUserHosting ? "Hosting" : "Online"}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OnlineUsersPanel;
